import CARMO_CONST from '@/common/util/const';
import { normalizeString2 as normalizeString } from '@/common/util/normalizeString';
import Contract from '@/newcar/core/model/contract';
import {
  questionnaires1Answers, questionnaires1Sub1Answers, questionnaires2Answers,
  questionnaires5Answers,
  questionnaires1, questionnaires1Sub1, questionnaires2,
  questionnaires5, questionnaires1Sub2, questionnaires1Sub2Answers,
} from '@/newcar/core/model/questionnaire';
import {
  ContractType,
  HOUSING_TYPE_FAMILY,
  HOUSING_TYPE,
  HOUSING_TYPE_2,
  YES_OR_NO,
  YES_OR_NO_2,
  YES_OR_NO_3,
  OTHER_INDUSTRY_TYPE,
  OCCUPATION_TYPE,
  OCCUPATION_TYPE_2,
  OLDEST_AGE,
  YOUNGEST_AGE,
  POSITION,
} from '@/newcar/util/const/contract';
import hiddenSections from '@/newcar/ui/contract/entities/HiddenSections';
import hiddenFields from '@/newcar/ui/contract/entities/HiddenFields';
import initializeFields from '@/newcar/ui/contract/entities/InitializeFields';
import scrollToField from '@/newcar/ui/contract/entities/ScrollToField';

const VALIDATE_MESSAGE_REQUIRED = '入力してください';
const VALIDATE_MESSAGE_EMAIL = 'カーリースご利用可否についてのご連絡を受信するアドレスを入力してください';
const VALIDATE_MESSAGE_RADIO = '１つ選択してください';
const VALIDATE_MESSAGE_RADIO_2 = 'どちらかを選択してください';
const VALIDATE_MESSAGE_RADIO_3 = 'いずれかを選択してください';
const VALIDATE_MESSAGE_SELECT = '選択してください';
const VALIDATE_MESSAGE_SELECT_2 = '１つ選択してください';
const VALIDATE_MESSAGE_CHECKBOX = '1つ以上選択してください';
const VALIDATE_MESSAGE_NUMBER = '半角数字での入力をお願いします';
const VALIDATE_MESSAGE_NUMBER_2 = '整数でお願いします';
const VALIDATE_MESSAGE_NUMBER_3 = '数字以外の使用できない文字が含まれています。確認の上修正をお願いします';
const VALIDATE_MESSAGE_KANA = 'カタカナ以外の使用できない文字が含まれています。確認の上修正をお願いします';
const VALIDATE_MESSAGE_JIS = '日本語・アルファベット・数字以外の使用できない文字が含まれています。確認の上修正をお願いします';
const VALIDATE_MESSAGE_BIRTHDAY = 'お申込の対象は 18 歳以上〜 75 歳以下です。';
const VALIDATE_MESSAGE_POSTAL_CODE = '郵便番号が正しくありません。確認の上修正をお願いします';
const VALIDATE_MESSAGE_ADDRESS = '郵便番号を入力してください';
const VALIDATE_MESSAGE_PHONE = '市外局番が無いようです。確認の上修正をお願いします';
const VALIDATE_MESSAGE_LICENSE = '免許証番号の桁数に誤りがあるようです。確認して修正をお願いします';
const VALIDATE_MESSAGE_LICENSE_CHECK_DIGIT = '正しい免許証番号を入力してください';
const VALIDATE_MESSAGE_MIN_LENGTH = '桁が短いようです。確認の上修正をお願いします';
const VALIDATE_MESSAGE_MAX_LENGTH = '桁が長すぎるようです。確認の上修正をお願いします';
const VALIDATE_MESSAGE_MAX_VALUE = '万円単位でお願いします（例：400万円→400）';
const VALIDATE_MESSAGE_MIN_VALUE = '1年以上でお願いします（例:4ヶ月→1）';
const VALIDATE_MESSAGE_UNUSABLE_NAME_WORD = '個人名をご入力ください';
const VALIDATE_MESSAGE_HOLDER = '所有者名をご記入ください';
export const ERROR_MESSAGE_REST_API = '正常に処理できませんでした。通信環境をお確かめの上、時間を置いて再度お試しください。';

type ComponentType = 'button' | 'title' | 'section';

// 申請フォーム用設定値
type SectionType = 'name' | 'phone' | 'email' | 'questionnaire' | 'self' |
  'address' | 'residence' | 'housing' | 'household' | 'work' | 'workplace' | 'license';
export type PrivacyType = 'terms' | 'privacy' | 'agreement';
interface ValidateRuleAttribute<T> {
  value: T,
  message: string;
  messages?: {minMessage: string; maxMessage: string;}; // 電話番号(phone)は複数メッセージを指定
  result?: boolean;
  override?: {
    target: keyof Contract;
    when: {
      type: 'equal' | 'not-equal' | 'less' | 'more';
      value: string | number;
    };
    message: string;
    value: T;
  }
}
export type ValidateRuleAttributeType = {
  required: ValidateRuleAttribute<boolean>;
  optional?: ValidateRuleAttribute<boolean>;
  maxLength?: ValidateRuleAttribute<number>;
  minLength?: ValidateRuleAttribute<number>;
  max?: ValidateRuleAttribute<number>;
  min?: ValidateRuleAttribute<number>;
  pattern?: ValidateRuleAttribute<RegExp> | ValidateRuleAttribute<RegExp>[];
  match?: ValidateRuleAttribute<string>;
  jis?: ValidateRuleAttribute<string>;
  phone?: ValidateRuleAttribute<RegExp>;
  age?: ValidateRuleAttribute<number[]>;
  holder?: ValidateRuleAttribute<string[]>;
  isNone?: boolean; // trueであればフォームに表示しない
  isReadOnly?: boolean; // trueであば読み取り専用
  limit?: {}; // inputに指定する制限
  licenseCheck?: ValidateRuleAttribute<boolean>;
}

export type ConvertTargetType = 'zenkaku' | 'number' | 'kana' | 'email' | 'phoneNumber' | 'zipCode';
export type InputType = 'text' | 'tel' | 'email' | 'radio' | 'select' | 'checkbox' | 'check' | 'address';

// 申込みフォームの各項目のonBlurに実行する変換内容、条件、対象
export type ConvertType = {
  function: (value: string, types: ConvertTargetType[]) => string,
  targets: ConvertTargetType[],
}

// 申込みフォームの各項目のonChangeに実行する内容、条件、対象
export type TriggerType = {
  function: Function,
  when?: {
    type: 'equal' | 'not-equal' | 'less' | 'more' | 'not-equal-length';
    value: string;
  };
  targets: (keyof Contract | SectionType)[],
}

// 申込みフォームの各項目のonFocusに実行する内容、対象
export type FocusType = {
  function: Function,
  target?: string, // 対象のIDを指定する。対象が自身の場合はtargetを指定しない
}

// 申込みフォームの各項目入力後の自動スクロールの設定値
export type ScrollNextType = {
  target?: string,
  subTarget?: string, // 特定条件の場合の対象
}

// 住所検索時の設定項目
export type AddressType = {
  targets: (keyof Contract)[],
}

export type BreakPoint = {
  mobile: number;
  tablet: number;
  desktop: number;
};

// 各項目の選択肢の内容
export type Item = {
  label: string;
  value: string | number;
  breakpoint?: BreakPoint;
  htmlId?: string;// 通常はローカルストレージキーと同じidを使うが、イレギュラーパターン用
};

export type ColumnType = {
  id: keyof Contract;
  subid?: string;// 住所検索でのみ使用する
  type: InputType;
  rule: ValidateRuleAttributeType;
  label: string;
  tooltip?: string;
  placeholder?: string;
  forwardHelperText1?: string;
  forwardHelperText2?: {
    value: string,
    when?: {
      type: 'equal' | 'not-equal';
      target: keyof Contract;
      value: string;
    };
  };
  helperText?: string;
  adornmentStart?: string;
  adornmentStartClass?: string;
  adornmentEnd?: string;
  convert?: ConvertType;
  triggers?: TriggerType[];
  focus?: FocusType;
  address?: AddressType;
  next?: ScrollNextType;
  items?: Item[];
  defaultValue?: string | number;
  decoration?: boolean;
  breakpoint: BreakPoint;
};

type PageConfigType = {
  [ComponentKey in ComponentType] : {
    [ContactKey in ContractType]: {
      label?: string;
      gtm?: string;
      targets?: SectionType[];
      id?: string;
    };
  }
}

export const COLUMN_CONFIG: {
  [Key in SectionType]: {
    title: string;
    balloon?: string;
    qa?: {q: string, a:string}[];
    columns: ColumnType[];
  }
} = {
  name: {
    title: 'お名前',
    columns: [
      {
        id: 'lastName',
        type: 'text',
        label: '姓（漢字）',
        placeholder: '例：山田',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.UNUSABLE_NAME_WORD,
            message: VALIDATE_MESSAGE_UNUSABLE_NAME_WORD,
          },
          jis: {
            value: '',
            message: VALIDATE_MESSAGE_JIS,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-name',
        },
        next: {
          target: 'form-section-name',
        },
        breakpoint: {
          mobile: 6,
          tablet: 6,
          desktop: 6,
        },
      },
      {
        id: 'firstName',
        type: 'text',
        label: '名（漢字）',
        placeholder: '例：太郎',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.UNUSABLE_NAME_WORD,
            message: VALIDATE_MESSAGE_UNUSABLE_NAME_WORD,
          },
          jis: {
            value: '',
            message: VALIDATE_MESSAGE_JIS,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-name',
        },
        next: {
          target: 'form-section-name',
        },
        breakpoint: {
          mobile: 6,
          tablet: 6,
          desktop: 6,
        },
      },
      {
        id: 'lastKana',
        type: 'text',
        label: 'セイ（カタカナ）',
        placeholder: '例：ヤマダ',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.KANA,
            message: VALIDATE_MESSAGE_KANA,
          },
          jis: {
            value: '',
            message: VALIDATE_MESSAGE_JIS,
          },
        },
        convert: {
          function: normalizeString,
          targets: ['kana'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-name',
        },
        next: {
          target: 'form-section-name',
        },
        breakpoint: {
          mobile: 6,
          tablet: 6,
          desktop: 6,
        },
      },
      {
        id: 'firstKana',
        type: 'text',
        label: 'メイ（カタカナ）',
        placeholder: '例：タロウ',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.KANA,
            message: VALIDATE_MESSAGE_KANA,
          },
          jis: {
            value: '',
            message: VALIDATE_MESSAGE_JIS,
          },
        },
        convert: {
          function: normalizeString,
          targets: ['kana'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-name',
        },
        next: {
          target: 'form-section-phone',
        },
        breakpoint: {
          mobile: 6,
          tablet: 6,
          desktop: 6,
        },
      },
    ],
  },
  phone: {
    title: 'ご連絡先電話番号',
    columns: [
      {
        id: 'phoneNumber',
        type: 'tel',
        label: '電話番号',
        tooltip: 'ハイフンは不要です',
        placeholder: '例：09012345678',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          phone: {
            value: CARMO_CONST.REGEX.MOBILE_PHONE,
            message: VALIDATE_MESSAGE_PHONE,
            messages: {
              minMessage: VALIDATE_MESSAGE_MIN_LENGTH,
              maxMessage: VALIDATE_MESSAGE_MAX_LENGTH,
            },
          },
        },
        convert: {
          function: normalizeString,
          targets: ['phoneNumber'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-phone',
        },
        next: {
          target: 'form-section-phone',
          subTarget: 'form-section-email',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  email: {
    title: 'メールアドレス',
    columns: [
      {
        id: 'email',
        type: 'email',
        label: 'メールアドレス',
        placeholder: '例：carmo@carmo-kun.jp',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_EMAIL,
          },
          pattern: [
            {
              value: /@/,
              message: '@がありませんでした。確認の上修正をお願いします',
            },
            {
              value: CARMO_CONST.REGEX.MAIL_PIRIOD,
              result: true,
              message: 'ドメインにピリオドがありませんでした。確認の上修正をお願いします',
            },
            {
              value: CARMO_CONST.REGEX.MAIL_INVALID_CHARACTERS,
              result: true,
              message: 'メールアドレスに使えない文字が含まれます。確認して修正をお願いします',
            },
            {
              value: CARMO_CONST.REGEX.MAIL,
              message: 'メールアドレスが正しくありません。確認の上修正をお願いします',
            },
          ],
        },
        convert: {
          function: normalizeString,
          targets: ['email'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-email',
        },
        next: {
          target: 'form-section-email',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  questionnaire: {
    title: 'アンケート',
    columns: [
      {
        id: 'question1Answer',
        type: 'radio',
        label: questionnaires1,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO,
          },
        },
        items: questionnaires1Answers.map((q) => ({
          label: q,
          value: q,
          htmlId: `question1_${q}`,
          breakpoint: { mobile: 12, tablet: 12, desktop: 12 },
        })),
        focus: {
          function: scrollToField,
          target: 'field-question1Sub1Answer',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
        decoration: true,
      },
      {
        id: 'question1Sub1Answer',
        type: 'checkbox',
        label: questionnaires1Sub1,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_CHECKBOX,
          },
        },
        focus: {
          function: scrollToField,
          target: 'field-question1Sub1Answer',
        },
        items: questionnaires1Sub1Answers.map((q) => ({
          label: q,
          value: q,
          htmlId: `question1_sub1_${q}`,
        })),
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
        decoration: true,
      },
      {
        id: 'question1Sub2Answer',
        type: 'checkbox',
        label: questionnaires1Sub2,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_CHECKBOX,
          },
        },
        focus: {
          function: scrollToField,
          target: 'field-question1Sub2Answer',
        },
        items: questionnaires1Sub2Answers.map((q) => ({
          label: q,
          value: q,
          htmlId: `question1_sub2_${q}`,
        })),
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
        decoration: true,
      },
      {
        id: 'question2Answer',
        type: 'radio',
        label: questionnaires2,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO,
          },
        },
        items: questionnaires2Answers.map((q) => ({
          label: q,
          value: q,
          htmlId: `question2_${q}`,
          breakpoint: { mobile: 12, tablet: 12, desktop: 12 },
        })),
        focus: {
          function: scrollToField,
          target: 'field-question5Answer',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
        decoration: true,
      },
      {
        id: 'question5Answer',
        type: 'radio',
        label: questionnaires5,
        adornmentStart: '（1つだけ選択可）',
        adornmentStartClass: 'has-text-weight-bold',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-name',
        },
        items: questionnaires5Answers.map((q) => ({
          label: q,
          value: q,
          htmlId: `question5_${q}`,
          breakpoint: { mobile: 12, tablet: 12, desktop: 12 },
        })),
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  self: {
    title: 'ご本人様情報',
    columns: [
      {
        id: 'gender',
        type: 'radio',
        label: '性別',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO_2,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-self',
        },
        items: [
          {
            label: CARMO_CONST.GENDER.MALE.LABEL,
            value: CARMO_CONST.GENDER.MALE.VALUE,
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
            htmlId: 'gender_0',
          },
          {
            label: CARMO_CONST.GENDER.FEMALE.LABEL,
            value: CARMO_CONST.GENDER.FEMALE.VALUE,
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
            htmlId: 'gender_1',
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'year',
        type: 'select',
        label: '生年月日',
        defaultValue: 0,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT,
          },
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['month', 'day'],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'form-section-self',
        },
        breakpoint: {
          mobile: 4,
          tablet: 4,
          desktop: 4,
        },
      },
      {
        id: 'month',
        type: 'select',
        label: '',
        defaultValue: 0,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT,
          },
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['day'],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'form-section-self',
        },
        breakpoint: {
          mobile: 4,
          tablet: 4,
          desktop: 4,
        },
      },
      {
        id: 'day',
        type: 'select',
        label: '',
        defaultValue: 0,
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT,
          },
          age: {
            value: [OLDEST_AGE, YOUNGEST_AGE],
            message: VALIDATE_MESSAGE_BIRTHDAY,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-self',
        },
        next: {
          target: 'form-section-address',
        },
        breakpoint: {
          mobile: 4,
          tablet: 4,
          desktop: 4,
        },
      },
    ],
  },
  address: {
    title: '現住所',
    qa: [
      {
        q: 'これから引っ越す場合、現住所と引っ越し先の住所どちらを記入すればいいですか',
        a: '審査申し込み時点での住所をご入力ください',
      },
    ],
    columns: [
      {
        id: 'postalCode',
        subid: 'addressManual',
        type: 'address',
        label: '郵便番号',
        helperText: '※郵便番号から自動で住所が反映されます',
        tooltip: 'ハイフンは不要です',
        placeholder: '例：1234567',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: [
            {
              value: CARMO_CONST.REGEX.NUMBER,
              message: VALIDATE_MESSAGE_NUMBER,
            },
            {
              value: CARMO_CONST.REGEX.POSTAL_CODE,
              message: VALIDATE_MESSAGE_POSTAL_CODE,
            },
          ],
          limit: {
            maxLength: '7',
          },
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-address',
        },
        address: {
          targets: ['prefectures', 'city', 'street', 'addressKana', 'address1'],
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['prefectures', 'city', 'street', 'addressKana', 'address1'],
          },
        ],
        items: [
          {
            label: '',
            value: '',
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
          {
            label: '住所検索',
            value: '',
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'prefectures', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'city', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'street', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'addressKana',
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'address1',
        type: 'text',
        label: '都道府県市区町村',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_ADDRESS,
          },
          isReadOnly: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'address3',
        type: 'text',
        label: '番地以降',
        tooltip: '丁目・番地以降を入力してください',
        placeholder: '例：1-23-4 定額カルモくんハイツ102号室',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
        },
        focus: {
          function: scrollToField,
          target: 'field-address3',
        },
        next: {
          target: 'field-isSameAddress',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'isSameAddress',
        type: 'radio',
        label: '「現住所」と「住民票の住所」は同一ですか',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO_2,
          },
        },
        triggers: [
          {
            function: hiddenFields,
            when: {
              type: 'not-equal',
              value: YES_OR_NO_3[1],
            },
            targets: [
              'addressPostalCode',
              'addressAddress1',
              'addressAddress3',
            ],
          },
          {
            function: hiddenSections,
            when: {
              type: 'not-equal',
              value: YES_OR_NO_3[1],
            },
            targets: [
              'residence',
            ],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'field-isSameAddress',
        },
        items: [
          {
            label: YES_OR_NO_2[0],
            value: YES_OR_NO_3[0],
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
            htmlId: 'isSameAddress_true',
          },
          {
            label: YES_OR_NO_2[1],
            value: YES_OR_NO_3[1],
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
            htmlId: 'isSameAddress_false',
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  residence: {
    title: '住民票の住所',
    columns: [
      {
        id: 'addressPostalCode',
        subid: 'addressAddressManual',
        type: 'address',
        label: '郵便番号',
        helperText: '※郵便番号から自動で住所が反映されます',
        tooltip: 'ハイフンは不要です',
        placeholder: '例：1234567',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: [
            {
              value: CARMO_CONST.REGEX.NUMBER,
              message: VALIDATE_MESSAGE_NUMBER,
            },
            {
              value: CARMO_CONST.REGEX.POSTAL_CODE,
              message: VALIDATE_MESSAGE_POSTAL_CODE,
            },
          ],
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-residence',
        },
        address: {
          targets: ['residencePrefectures', 'residenceCity', 'residenceStreet', 'residenceAddressKana', 'addressAddress1', 'addressAddress3'],
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['residencePrefectures', 'residenceCity', 'residenceStreet', 'residenceAddressKana', 'addressAddress1', 'addressAddress3'],
          },
        ],
        items: [
          {
            label: '',
            value: '',
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
          {
            label: '住所検索',
            value: '',
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'residencePrefectures', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'residenceCity', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'residenceStreet', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'residenceAddressKana',
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'addressAddress1',
        type: 'text',
        label: '都道府県市区町村',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_ADDRESS,
          },
          isReadOnly: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'addressAddress3',
        type: 'text',
        label: '番地以降',
        tooltip: '丁目・番地以降を入力してください',
        placeholder: '例：1-23-4 定額カルモくんハイツ102号室',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
        },
        focus: {
          function: scrollToField,
          target: 'field-addressAddress3',
        },
        next: {
          target: 'form-section-housing',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  housing: {
    title: 'お住まい',
    columns: [
      {
        id: 'residenceYear',
        type: 'tel',
        label: '居住年数',
        placeholder: '例：1',
        adornmentStart: '※１年未満は「1」とご記入ください',
        adornmentStartClass: 'is-size-6 is-full is-negativ-margin-top-2 is-margin-bottom-3',
        adornmentEnd: '年',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.NUMBER,
            message: VALIDATE_MESSAGE_NUMBER_2,
          },
          min: {
            value: 1,
            message: VALIDATE_MESSAGE_MIN_VALUE,
          },
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-housing',
        },
        next: {
          target: 'field-residenceStatus',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'residenceStatus',
        type: 'radio',
        label: '居住形態',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO,
          },
        },
        items: HOUSING_TYPE.map((q, index) => ({
          label: q,
          value: HOUSING_TYPE_2[index],
          breakpoint: { mobile: 12, tablet: 12, desktop: 12 },
          htmlId: `residenceStatus_${index}`,
        })),
        triggers: [
          {
            function: hiddenFields,
            when: {
              type: 'not-equal',
              value: HOUSING_TYPE_FAMILY,
            },
            targets: [
              'holder',
            ],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'field-residenceStatus',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'holder',
        type: 'text',
        label: '',
        adornmentStart: '家族所有の場合、名義人氏名を入力してください',
        adornmentStartClass: 'is-full',
        placeholder: '例：山田太郎',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          holder: {
            value: ['lastName', 'firstName'],
            message: VALIDATE_MESSAGE_HOLDER,
          },
        },
        focus: {
          function: scrollToField,
          target: 'field-holder',
        },
        next: {
          target: 'form-section-household',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  household: {
    title: '世帯情報',
    columns: [
      {
        id: 'maritalStatus',
        type: 'radio',
        label: '配偶者',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO_2,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-household',
        },
        items: [
          {
            label: YES_OR_NO[0],
            value: YES_OR_NO_3[0],
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
            htmlId: 'maritalStatus_true',
          },
          {
            label: YES_OR_NO[1],
            value: YES_OR_NO_3[1],
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
            htmlId: 'maritalStatus_false',
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'livingPersons',
        type: 'select',
        label: '同居人数',
        helperText: '※ご本人様を含む同居人数を選択してください',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT_2,
          },
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['livingMinors'],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'form-section-household',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'livingMinors',
        type: 'select',
        label: '同居している未成年者数',
        helperText: '※同居している未成年者数（18歳未満）を選択してください',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT_2,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-household',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  work: {
    title: 'お勤め先情報',
    columns: [
      {
        id: 'businessType',
        type: 'radio',
        label: '種別',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_RADIO_3,
          },
        },
        triggers: [
          {
            function: initializeFields,
            when: {
              type: 'equal',
              value: OTHER_INDUSTRY_TYPE,
            },
            targets: [
              'industryType',
              'subIndustryType',
            ],
          },
          {
            function: hiddenFields,
            when: {
              type: 'not-equal',
              value: OCCUPATION_TYPE_2[1],
            },
            targets: [
              'position',
            ],
          },
          {
            function: hiddenFields,
            when: {
              type: 'equal',
              value: OCCUPATION_TYPE_2[2],
            },
            targets: [
              'company',
              'companyPostalCode',
              'companyAddress1',
              'companyAddress3',
              'companyPhoneNumber',
            ],
          },
          {
            function: hiddenSections,
            when: {
              type: 'equal',
              value: OCCUPATION_TYPE_2[2],
            },
            targets: [
              'workplace',
            ],
          },
          {
            function: hiddenFields,
            when: {
              type: 'not-equal',
              value: OCCUPATION_TYPE_2[0],
            },
            targets: [
              'businessAddressType',
            ],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'form-section-work',
        },
        items: [
          {
            label: OCCUPATION_TYPE[0],
            value: OCCUPATION_TYPE_2[0],
            breakpoint: {
              mobile: 12,
              tablet: 12,
              desktop: 12,
            },
            htmlId: 'businessType_0',
          },
          {
            label: OCCUPATION_TYPE[1],
            value: OCCUPATION_TYPE_2[1],
            breakpoint: {
              mobile: 12,
              tablet: 12,
              desktop: 12,
            },
            htmlId: 'businessType_1',
          },
          {
            label: OCCUPATION_TYPE[2],
            value: OCCUPATION_TYPE_2[2],
            breakpoint: {
              mobile: 12,
              tablet: 12,
              desktop: 12,
            },
            htmlId: 'businessType_2',
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'industryType',
        type: 'select',
        label: '業種（大分類）',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT_2,
          },
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['subIndustryType'],
          },
        ],
        focus: {
          function: scrollToField,
          target: 'field-industryType',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'subIndustryType',
        type: 'radio',
        label: '業種（小分類）',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT_2,
          },
        },
        items: [], // 業種（大分類）によって値が変わるためconfigでは記述しない
        focus: {
          function: scrollToField,
          target: 'field-position',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'position',
        type: 'radio',
        label: '雇用形態',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_SELECT_2,
          },
        },
        items: POSITION.map((p) => ({
          label: p,
          value: p,
          htmlId: `position_${p}`,
          breakpoint: { mobile: 6, tablet: 6, desktop: 6 },
        })),
        focus: {
          function: scrollToField,
          target: 'field-position',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'businessYears',
        type: 'tel',
        label: '勤続年数（年）',
        tooltip: '勤続何年目かを入力してください',
        placeholder: '2',
        adornmentEnd: '年',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.NUMBER,
            message: VALIDATE_MESSAGE_NUMBER_2,
          },
          min: {
            value: 0,
            message: VALIDATE_MESSAGE_MIN_VALUE,
          },
          limit: {
            maxLength: '2',
          },
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'field-businessYears',
        },
        next: {
          target: 'field-businessYears',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'income',
        type: 'tel',
        label: '年収',
        placeholder: '200',
        adornmentEnd: '万円',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: {
            value: CARMO_CONST.REGEX.NUMBER,
            message: VALIDATE_MESSAGE_NUMBER,
          },
          max: {
            value: 99999,
            message: VALIDATE_MESSAGE_MAX_VALUE,
          },
          limit: {
            maxLength: '10',
          },
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'field-businessYears',
        },
        next: {
          target: 'form-section-license',
          subTarget: 'form-section-workplace',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  workplace: {
    title: 'お勤め先の情報',
    balloon: '会社名で自動入力！',
    columns: [
      {
        id: 'businessAddressType',
        type: 'check',
        label: '現住所とお勤め先住所が同じ',
        items: [{ label: '現住所とお勤め先住所が同じ', value: '1' }],
        rule: {
          required: {
            value: false,
            message: '',
          },
        },
        triggers: [
          {
            function: hiddenFields,
            when: {
              type: 'equal',
              value: '1',
            },
            targets: [
              'companyPostalCode',
              'companyAddress1',
              'companyAddress3',
            ],
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'company',
        type: 'text',
        label: 'お勤め先（会社名・屋号）',
        placeholder: '例：株式会社定額カルモくん',
        forwardHelperText1: '※自営及びお勤めの方で屋号がない場合は「屋号なし」とご記入ください',
        forwardHelperText2: {
          value: '※派遣社員の方は派遣元の情報をご入力下さい',
          when: {
            type: 'equal',
            target: 'businessType',
            value: OCCUPATION_TYPE_2[1],
          },
        },
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
        },
        focus: {
          function: scrollToField,
          target: 'form-section-workplace',
        },
        next: {
          target: 'field-companyPostalCode',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'companyPostalCode',
        subid: 'companyAddressManual',
        type: 'address',
        label: '郵便番号',
        tooltip: 'ハイフンは不要です',
        placeholder: '例：1234567',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: [
            {
              value: CARMO_CONST.REGEX.NUMBER,
              message: VALIDATE_MESSAGE_NUMBER,
            },
            {
              value: CARMO_CONST.REGEX.POSTAL_CODE,
              message: VALIDATE_MESSAGE_POSTAL_CODE,
            },
          ],
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'field-companyPostalCode',
        },
        address: {
          targets: ['officePrefectures', 'officeCity', 'officeStreet', 'officeAddressKana', 'companyAddress1'],
        },
        triggers: [
          {
            function: initializeFields,
            targets: ['officePrefectures', 'officeCity', 'officeStreet', 'officeAddressKana', 'companyAddress1'],
          },
        ],
        items: [
          {
            label: '',
            value: '',
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
          {
            label: '住所検索',
            value: '',
            breakpoint: {
              mobile: 6,
              tablet: 6,
              desktop: 6,
            },
          },
        ],
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'officePrefectures', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'officeCity', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'officeStreet', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'officeAddressKana', // 自動入力用、HTMLには表示しない
        type: 'text',
        label: '',
        rule: {
          required: {
            value: false,
            message: '',
          },
          isNone: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'companyAddress1',
        type: 'text',
        label: '都道府県市区町村',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_ADDRESS,
          },
          isReadOnly: true,
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      {
        id: 'companyAddress3',
        type: 'text',
        label: '番地以降',
        forwardHelperText1: '※マンション名、部屋番号まで省略せずに入力してください',
        tooltip: '丁目・番地以降を入力してください',
        placeholder: '例：1-23-4 定額カルモくんハイツ102号室',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
        },
        focus: {
          function: scrollToField,
          target: 'field-companyAddress3',
        },
        next: {
          target: 'field-companyPhoneNumber',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
      // お勤め先お電話番号
      {
        id: 'companyPhoneNumber',
        type: 'tel',
        label: 'お勤め先お電話番号',
        placeholder: '例：0312345678',
        helperText: '※内容確認のためにお勤め先に連絡することはありません',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          phone: {
            value: CARMO_CONST.REGEX.MOBILE_PHONE,
            message: VALIDATE_MESSAGE_PHONE,
            messages: {
              minMessage: VALIDATE_MESSAGE_MIN_LENGTH,
              maxMessage: VALIDATE_MESSAGE_MAX_LENGTH,
            },
          },
        },
        convert: {
          function: normalizeString,
          targets: ['phoneNumber'],
        },
        focus: {
          function: scrollToField,
          target: 'field-companyPhoneNumber',
        },
        next: {
          target: 'form-section-license',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
  license: {
    title: '免許証番号',
    balloon: 'これが最後です！',
    columns: [
      {
        id: 'licenseNumber',
        type: 'tel',
        label: '',
        placeholder: '123456789012',
        adornmentStart: '第',
        adornmentStartClass: 'is-padding-right-3 is-nallow',
        adornmentEnd: '号',
        rule: {
          required: {
            value: true,
            message: VALIDATE_MESSAGE_REQUIRED,
          },
          pattern: [
            {
              value: CARMO_CONST.REGEX.NUMBER,
              message: VALIDATE_MESSAGE_NUMBER_3,
            },
            {
              value: CARMO_CONST.REGEX.LICENSE_NUMBER_NO_HYPHEN,
              message: VALIDATE_MESSAGE_LICENSE,
            },
          ],
          licenseCheck: {
            value: true,
            message: VALIDATE_MESSAGE_LICENSE_CHECK_DIGIT,
          },
          limit: {
            maxLength: '12',
          },
        },
        convert: {
          function: normalizeString,
          targets: ['number'],
        },
        focus: {
          function: scrollToField,
          target: 'form-section-license',
        },
        breakpoint: {
          mobile: 12,
          tablet: 12,
          desktop: 12,
        },
      },
    ],
  },
};

export const PAGE_CONFIG: PageConfigType = {
  title: {
    contact_info: {
      label: 'ご連絡先',
    },
    personal_info: {
      label: 'お客様情報(1/2)',
    },
    review_info: {
      label: 'お客様情報(2/2)',
    },
    provisional_completion: {
      label: 'お申込ありがとうございました',
    },
  },
  button: {
    contact_info: {
      label: '次のステップへ進む',
      gtm: 'gtm-newcarform-contactInfoSubmit',
    },
    personal_info: {
      label: '最後のステップへ進む',
      gtm: 'gtm-newcarform-personalInfoSubmit',
    },
    review_info: {
      label: '上記の内容で送信する',
      gtm: 'gtm-newcarform-reviewInfoSubmit',
    },
    provisional_completion: {
      label: '定額カルモくん 中古車TOPへ',
    },
  },
  section: {
    contact_info: {
      targets: ['questionnaire', 'name', 'phone', 'email'],
    },
    personal_info: {
      targets: ['self', 'address', 'residence', 'housing', 'household'],
    },
    review_info: {
      targets: ['work', 'workplace', 'license'],
    },
    provisional_completion: {
      targets: [],
    },
  },
};

export const META_TITLES: { [ContactKey in ContractType]: string } = {
  contact_info: 'ご連絡先入力 - おトクにマイカー 定額カルモくん',
  personal_info: 'お客様情報入力 - おトクにマイカー 定額カルモくん',
  review_info: 'お客様情報入力 - おトクにマイカー 定額カルモくん',
  provisional_completion: '審査申込完了 - おトクにマイカー 定額カルモくん',
};

export const META_DESCRIPTION: { [ContactKey in ContractType]: string } = {
  contact_info: '会員情報についてのご連絡先情報入力ページです。',
  personal_info: '会員情報についてのお客様情報入力ページです。',
  review_info: 'お客様情報についての入力ページです。',
  provisional_completion: '審査申込の完了ページです。',
};

export const FORM_IDS: { [ContactKey in ContractType]: { id: string } } = {
  contact_info: {
    id: '',
  },
  personal_info: {
    id: 'form-personal-info',
  },
  review_info: {
    id: 'form-review-info',
  },
  provisional_completion: {
    id: '',
  },
};

export const PRIVACY_LINKS: {
  [Key in PrivacyType]:{ href: string, label: string, gap: string, gtm: string }
} = {
  agreement: {
    href: 'https://carmo-kun.jp/docs/agreement/',
    label: 'その他同意条項',
    gap: ' に同意して',
    gtm: 'gtm-newcarform-agreements',
  },
  terms: {
    href: 'https://carmo-kun.jp/docs/terms/',
    label: 'おトクにマイカー 定額カルモくん利用規約',
    gap: ' 、',
    gtm: 'gtm-newcarform-terms',
  },
  privacy: {
    href: 'https://nyle.co.jp/privacypolicy/',
    label: 'プライバシーポリシー',
    gap: ' 及び、',
    gtm: 'gtm-newcarform-privacy',
  },
};
